import { useCallback, useReducer } from 'react';

import type {
  AccountData,
  DomainMatch,
  PendingProjectInvite,
  SelfServiceSMBSignupAction,
  SelfServiceSMBSignupState,
  SubscriptionResult,
} from '../types';

// TODO(ENG-5201 follow-up): the reducer carries an `error` field that no
// step transition actually depends on — every step component manages its
// own local error state already. Removing `state.error` (+ setError /
// clearError actions) collapses three error-storage mechanisms into one.
// Deferred from the initial PR because the change touches ~7 files and
// their tests; track as follow-up cleanup once this PR ships.

export const INITIAL_STATE: SelfServiceSMBSignupState = {
  step: 'account',
  account: {},
  emailOtpToken: null,
  emailOtpExpiresAt: null,
  emailVerified: false,
  domainMatches: [],
  domainMatchLoading: false,
  selectedMatch: null,
  pendingInvite: null,
  declinedInviteId: null,
  paymentMethodId: null,
  idempotencyKey: null,
  subscriptionResult: null,
  joinAutoApproved: false,
  error: null,
};

// Shared by `setDomainMatches` and `resolveEmailOtpVerification` (the latter
// falls through to this once it has ruled out a pending invite) so the
// 0/1/2+ branching lives in exactly one place. Also used by `declineInvite`,
// which is why `error` is cleared here too (ENG-5334 review fix #8): a
// stale error set on invite-accept (e.g. a failed "Join for free now")
// must not carry over into the team-create/team-join form that follows.
function domainMatchBranch(
  state: SelfServiceSMBSignupState,
  matches: DomainMatch[]
): SelfServiceSMBSignupState {
  if (matches.length === 0) {
    return {
      ...state,
      step: 'team-create',
      domainMatches: [],
      selectedMatch: null,
      domainMatchLoading: false,
      error: null,
    };
  }
  if (matches.length === 1) {
    return {
      ...state,
      step: 'team-create',
      domainMatches: matches,
      selectedMatch: matches[0] ?? null,
      domainMatchLoading: false,
      error: null,
    };
  }
  return {
    ...state,
    step: 'team-join',
    domainMatches: matches,
    selectedMatch: null,
    domainMatchLoading: false,
    error: null,
  };
}

// The step after a verified email: a pending invite wins over the
// domain-match count. Shared by resolveEmailOtpVerification and, for a
// social signup the api already verified, accountVerified (ENG-6166).
function verifiedEmailBranch(
  state: SelfServiceSMBSignupState,
  invite: PendingProjectInvite | null,
  domainMatches: DomainMatch[]
): SelfServiceSMBSignupState {
  // Decline has no server-side counterpart (see declinedInviteId's
  // comment in types.ts), so pendingProjectInvite keeps returning the
  // same invite for the rest of the session. Treat that invite as if
  // it weren't found at all rather than routing straight back to
  // invite-accept.
  const pendingInvite = invite && invite.id === state.declinedInviteId ? null : invite;
  if (pendingInvite) {
    // Cache domainMatches even though it doesn't drive the step here,
    // declineInvite below needs it if the user says this isn't their
    // invite, without re-fetching.
    return {
      ...state,
      step: 'invite-accept',
      pendingInvite,
      domainMatches,
      domainMatchLoading: false,
      error: null,
    };
  }
  return domainMatchBranch(state, domainMatches);
}

function selfServiceSMBSignupReducer(
  state: SelfServiceSMBSignupState,
  action: SelfServiceSMBSignupAction
): SelfServiceSMBSignupState {
  switch (action.type) {
    case 'accountCreated': {
      // No-op once the user has progressed past account/email-otp — re-firing
      // this action would replay the form data and lose later state.
      if (state.step !== 'account') return state;
      return {
        ...state,
        step: 'email-otp',
        account: action.data,
        error: null,
      };
    }

    case 'accountVerified': {
      // Same guard as accountCreated: only the account step creates one.
      if (state.step !== 'account') return state;
      return verifiedEmailBranch(
        { ...state, account: action.data, emailVerified: true },
        action.pendingInvite,
        action.domainMatches
      );
    }

    case 'verifyEmailOtp': {
      // Only meaningful on the email-otp step. setDomainMatches arrives
      // separately (the resolver fires groupsByEmailDomain after this).
      if (state.step !== 'email-otp') return state;
      return {
        ...state,
        emailOtpToken: action.token,
        emailOtpExpiresAt: action.expiresAt,
        emailVerified: true,
        domainMatchLoading: true,
        error: null,
      };
    }

    case 'setDomainMatches': {
      return domainMatchBranch(state, action.matches);
    }

    case 'resolveEmailOtpVerification': {
      // Owner of the post-verification step decision: a pending invite
      // always wins over domain-match count. Two async dispatch sites call
      // this action (EmailOtpStep's post-verify path and the modal's
      // resume effect), and both can fire during one mount, so this case
      // is guarded like the other step-setting cases below, only
      // email-otp (post-verify) and team-create/team-join (resume) are
      // valid source steps.
      if (
        state.step !== 'email-otp' &&
        state.step !== 'team-create' &&
        state.step !== 'team-join'
      ) {
        return state;
      }
      return verifiedEmailBranch(state, action.pendingInvite, action.domainMatches);
    }

    case 'declineInvite': {
      // Only meaningful from invite-accept: a stray dispatch from a later
      // step (e.g. subscription) must not knock the user back to
      // team-create/team-join.
      if (state.step !== 'invite-accept') return state;
      return {
        ...domainMatchBranch(state, state.domainMatches),
        pendingInvite: null,
        declinedInviteId: state.pendingInvite?.id ?? state.declinedInviteId,
      };
    }

    case 'domainMatchLoading': {
      return { ...state, domainMatchLoading: action.loading };
    }

    case 'otpExpired': {
      // Token expired on a downstream call (e.g. groupsByEmailDomain after
      // domain-match prefetch). Bounce back to email-otp so the user can
      // request a fresh code; preserve form data.
      return {
        ...state,
        step: 'email-otp',
        emailOtpToken: null,
        emailOtpExpiresAt: null,
        emailVerified: false,
        domainMatchLoading: false,
      };
    }

    case 'chooseDomainMatch': {
      return { ...state, selectedMatch: action.match };
    }

    case 'joinRequested': {
      if (state.step !== 'team-create' && state.step !== 'team-join') return state;
      return {
        ...state,
        step: 'team-request-sent',
        selectedMatch: action.match,
        joinAutoApproved: action.autoApproved,
        error: null,
      };
    }

    case 'restartTeamFromJoin': {
      // Only meaningful from the team-join branch (the multi-match picker
      // step) or from team-create when a matched single-team join failed.
      // From anywhere else this is a no-op so stray clicks can't reset
      // mid-payment.
      if (state.step !== 'team-join' && state.step !== 'team-create') return state;
      return {
        ...state,
        step: 'team-create',
        domainMatches: [],
        selectedMatch: null,
        error: null,
      };
    }

    case 'submitTeamCreate': {
      if (state.step !== 'team-create') return state;
      return {
        ...state,
        step: 'subscription',
        account: { ...state.account, companyName: action.companyName },
        error: null,
      };
    }

    case 'setPaymentMethodId': {
      return { ...state, paymentMethodId: action.paymentMethodId };
    }

    case 'setIdempotencyKey': {
      return { ...state, idempotencyKey: action.idempotencyKey };
    }

    case 'completeSubscription': {
      if (state.step !== 'subscription') return state;
      return {
        ...state,
        step: 'confirmation',
        subscriptionResult: action.result,
        error: null,
      };
    }

    case 'goBack': {
      if (state.step === 'subscription') {
        return { ...state, step: 'team-create', error: null };
      }
      if (state.step === 'email-otp') {
        // "Edit details" — preserve form data, drop OTP state.
        return {
          ...state,
          step: 'account',
          emailOtpToken: null,
          emailOtpExpiresAt: null,
          emailVerified: false,
          domainMatchLoading: false,
          error: null,
        };
      }
      return state;
    }

    case 'setError': {
      return { ...state, error: { code: action.code, message: action.message } };
    }

    case 'clearError': {
      return { ...state, error: null };
    }

    default: {
      const exhaustiveCheck: never = action;
      return exhaustiveCheck;
    }
  }
}

export interface SelfServiceSMBSignupFlow {
  state: SelfServiceSMBSignupState;
  dispatch: React.Dispatch<SelfServiceSMBSignupAction>;
  accountCreated: (data: AccountData) => void;
  accountVerified: (
    data: AccountData,
    pendingInvite: PendingProjectInvite | null,
    domainMatches: DomainMatch[]
  ) => void;
  verifyEmailOtp: (token: string, expiresAt: number) => void;
  resolveEmailOtpVerification: (
    pendingInvite: PendingProjectInvite | null,
    domainMatches: DomainMatch[]
  ) => void;
  setDomainMatches: (matches: DomainMatch[]) => void;
  setDomainMatchLoading: (loading: boolean) => void;
  otpExpired: () => void;
  chooseDomainMatch: (match: DomainMatch) => void;
  joinRequested: (match: DomainMatch, autoApproved: boolean) => void;
  restartTeamFromJoin: () => void;
  submitTeamCreate: (companyName: string) => void;
  setPaymentMethodId: (paymentMethodId: string) => void;
  setIdempotencyKey: (idempotencyKey: string) => void;
  completeSubscription: (result: SubscriptionResult) => void;
  goBack: () => void;
  declineInvite: () => void;
  setError: (code: string, message: string) => void;
  clearError: () => void;
}

export function useSelfServiceSMBSignupFlow(
  initial: Partial<SelfServiceSMBSignupState> = {}
): SelfServiceSMBSignupFlow {
  const [state, dispatch] = useReducer(selfServiceSMBSignupReducer, {
    ...INITIAL_STATE,
    ...initial,
  });

  const accountCreated = useCallback(
    (data: AccountData) => dispatch({ type: 'accountCreated', data }),
    []
  );
  const accountVerified = useCallback(
    (data: AccountData, pendingInvite: PendingProjectInvite | null, domainMatches: DomainMatch[]) =>
      dispatch({ type: 'accountVerified', data, pendingInvite, domainMatches }),
    []
  );
  const verifyEmailOtp = useCallback(
    (token: string, expiresAt: number) => dispatch({ type: 'verifyEmailOtp', token, expiresAt }),
    []
  );
  const resolveEmailOtpVerification = useCallback(
    (pendingInvite: PendingProjectInvite | null, domainMatches: DomainMatch[]) =>
      dispatch({ type: 'resolveEmailOtpVerification', pendingInvite, domainMatches }),
    []
  );
  const setDomainMatches = useCallback(
    (matches: DomainMatch[]) => dispatch({ type: 'setDomainMatches', matches }),
    []
  );
  const setDomainMatchLoading = useCallback(
    (loading: boolean) => dispatch({ type: 'domainMatchLoading', loading }),
    []
  );
  const otpExpired = useCallback(() => dispatch({ type: 'otpExpired' }), []);
  const chooseDomainMatch = useCallback(
    (match: DomainMatch) => dispatch({ type: 'chooseDomainMatch', match }),
    []
  );
  const joinRequested = useCallback(
    (match: DomainMatch, autoApproved: boolean) =>
      dispatch({ type: 'joinRequested', match, autoApproved }),
    []
  );
  const restartTeamFromJoin = useCallback(() => dispatch({ type: 'restartTeamFromJoin' }), []);
  const submitTeamCreate = useCallback(
    (companyName: string) => dispatch({ type: 'submitTeamCreate', companyName }),
    []
  );
  const setPaymentMethodId = useCallback(
    (paymentMethodId: string) => dispatch({ type: 'setPaymentMethodId', paymentMethodId }),
    []
  );
  const setIdempotencyKey = useCallback(
    (idempotencyKey: string) => dispatch({ type: 'setIdempotencyKey', idempotencyKey }),
    []
  );
  const completeSubscription = useCallback(
    (result: SubscriptionResult) => dispatch({ type: 'completeSubscription', result }),
    []
  );
  const goBack = useCallback(() => dispatch({ type: 'goBack' }), []);
  const declineInvite = useCallback(() => dispatch({ type: 'declineInvite' }), []);
  const setError = useCallback(
    (code: string, message: string) => dispatch({ type: 'setError', code, message }),
    []
  );
  const clearError = useCallback(() => dispatch({ type: 'clearError' }), []);

  return {
    state,
    dispatch,
    accountCreated,
    accountVerified,
    verifyEmailOtp,
    resolveEmailOtpVerification,
    setDomainMatches,
    setDomainMatchLoading,
    otpExpired,
    chooseDomainMatch,
    joinRequested,
    restartTeamFromJoin,
    submitTeamCreate,
    setPaymentMethodId,
    setIdempotencyKey,
    completeSubscription,
    goBack,
    declineInvite,
    setError,
    clearError,
  };
}
